import { createSignal, createEffect, onCleanup, Show, createMemo, For } from "solid-js";
import { hexColor, Text, View } from "@lightningtv/solid";
import { clientBranding } from "../../config/clientConfig";
import { playListPassword, playListUrl, playListUsername, setGlobalStream } from "../../../state";
import axios from "axios";
import { Buffer } from "buffer";
import { dnsResolver } from "../../api/providers/dnsResolver";
import { fixImageUrl } from "../../utils/fixImageUrl";

const OSD = (props: any) => {
  const [epgData, setEpgData] = createSignal<any>(null);
  const [listOfFav, setListOfFav] = createSignal<any>({});
  const [isFavChannel, setIsFavChannel] = createSignal(false);

  // Live clock for the top-right corner — ticks every 30s, which is plenty
  // for a "current time" display and avoids re-rendering every second.
  const [now, setNow] = createSignal(new Date());
  const clockInterval = setInterval(() => setNow(new Date()), 30000);
  onCleanup(() => clearInterval(clockInterval));

  const currentTime = createMemo(() =>
    now().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
  );
  const currentDate = createMemo(() =>
    now().toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })
  );

  function formatTimeRange(start: string, end: string) {
    const format = (str: string) => {
      if (!str) return null;
      const date = new Date(str.replace(" ", "T"));
      if (isNaN(date.getTime())) return null;
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${hours}:${minutes}`;
    };

    const startTime = format(start);
    const endTime = format(end);

    if (!startTime || !endTime) return null;
    return `${startTime} - ${endTime}`;
  }

  createEffect(async () => {
    const username = playListUsername();
    const password = playListPassword();
    const playList = playListUrl();
    const vpnStatus = localStorage.getItem("vpn");
    const proxyUrl = localStorage.getItem("proxy_url");

    if (!props.channel?.stream_id) return;

    setGlobalStream(props.channel.stream_id);

    let originalApiUrl = `${playList}/player_api.php?username=${username}&password=${password}&action=get_short_epg&stream_id=${props.channel.stream_id}`;
    let finalApiUrl = originalApiUrl;
    let headers = {};

    try {
      const dnsResult = await dnsResolver.transformUrl(originalApiUrl);
      if (typeof dnsResult === 'object' && dnsResult.url) {
        finalApiUrl = dnsResult.url;
        headers['Host'] = dnsResult.originalHost;
      } else if (typeof dnsResult === 'string' && dnsResult !== originalApiUrl) {
        finalApiUrl = dnsResult;
      }
    } catch (dnsError) {
      console.warn("⚠️ DNS transformation failed for EPG API:", dnsError);
      finalApiUrl = originalApiUrl;
    }

    if (vpnStatus === "on" && proxyUrl) {
      const bufferedUrl = Buffer.from(finalApiUrl, "utf-8").toString("base64");
      finalApiUrl = `${proxyUrl}${bufferedUrl}`;
    }

    axios
      .get(finalApiUrl, { headers })
      .then(response => {
        setEpgData(response.data);
      })
      .catch(error => {
        console.error(`❌ EPG API call failed:`, error);
        setEpgData(null);
      });
  });

  const firstProgram = createMemo(() => {
    const data = epgData()?.epg_listings;
    const program = data?.[0] ? atob(data[0].title) : "";
    return program || `Programs of ${props.channel?.name || "Channel"}`;
  });

  // Time range of the program currently playing — reuses the same
  // formatTimeRange used for the upcoming ones, so "now" and "next" read
  // consistently instead of only "next" showing a time.
  const firstProgramTime = createMemo(() => {
    const data = epgData()?.epg_listings;
    return data?.[0] ? formatTimeRange(data[0].start, data[0].stop) : null;
  });

  // Every program after the current one — however many the EPG actually
  // returns, not a hand-picked "second" and "third". Rendered with a <For>
  // below instead of two copy-pasted blocks.
  const upcomingPrograms = createMemo(() => {
    const data = epgData()?.epg_listings;
    if (!data) return [];

    return data
      .slice(1)
      .map((item: any) => ({
        title: atob(item.title),
        time: formatTimeRange(item.start, item.stop),
      }))
      .filter((program) => program.title && program.time);
  });

  function truncateText(str: string, maxLength = 28) {
    return str.length > maxLength ? str.slice(0, maxLength) + "..." : str;
  }

  const addToFavorites = () => {
    const currentPlaylistUrl = playListUrl();
    const existingChannels = localStorage.getItem("@favoriteChannels");
    let existingChannelsArray = existingChannels ? JSON.parse(existingChannels) : [];

    const isChannelExist = existingChannelsArray.some(
      (existingChannel) =>
        existingChannel.stream_id === props.channel.stream_id &&
        existingChannel.playlistUrl === currentPlaylistUrl
    );

    if (!isChannelExist) {
      const channelWithPlaylist = {
        ...props.channel,
        playlistUrl: currentPlaylistUrl
      };
      existingChannelsArray.push(channelWithPlaylist);
    } else {
      existingChannelsArray = existingChannelsArray.filter(
        (existingChannel) => !(
          existingChannel.stream_id === props.channel.stream_id &&
          existingChannel.playlistUrl === currentPlaylistUrl
        )
      );
    }

    localStorage.setItem("@favoriteChannels", JSON.stringify(existingChannelsArray));

    const currentPlaylistFavorites = existingChannelsArray.filter(
      (favChannel) => favChannel.playlistUrl === currentPlaylistUrl
    );

    setListOfFav(currentPlaylistFavorites);
    setIsFavChannel(
      currentPlaylistFavorites.some((favChannel) => favChannel.stream_id === props.channel.stream_id)
    );
  };

  createEffect(() => {
    if (props.channel?.stream_id) {
      const currentPlaylistUrl = playListUrl();
      const existingChannels = localStorage.getItem("@favoriteChannels");
      const existingChannelsArray = existingChannels ? JSON.parse(existingChannels) : [];

      const isFavorite = existingChannelsArray.some(
        (favChannel) =>
          favChannel.stream_id === props.channel.stream_id &&
          favChannel.playlistUrl === currentPlaylistUrl
      );
      setIsFavChannel(isFavorite);
    }
  });

  function calculateProgress(startTimeUnix: any, endTimeUnix: any) {
    let currentTime = Date.now() / 1000;
    let totalDuration = endTimeUnix - startTimeUnix;
    let elapsedDuration = currentTime - startTimeUnix;
    let progress = elapsedDuration / totalDuration;
    return Math.min(1, Math.max(0, progress));
  }

  return (
    <View>
      {/* Soft shade above the OSD — fades from transparent into the OSD's
          own black, so the panel doesn't cut against the video with a hard
          edge. Reuses the same top-to-bottom scrim asset used on the
          Entity page rather than a flat semi-transparent rectangle, which
          would itself have a hard edge at its own top. */}
      <View
        x={0}
        y={680 - 140}
        zIndex={2000}
        width={1920}
        height={140}
        src="assets/entity-scrim-bottom.png"
      />

      {/* Main OSD Container - Android TV Style */}
      <View
        x={0}
        y={680}
        zIndex={2001}
        width={1920}
        height={400}
        color={hexColor("#000000")}
        alpha={0.92}
      >
        {/* Top Section: Channel Info */}
        <View x={60} y={50}>
          {/* Channel Logo/Icon - Primary Position */}
          <Show when={props.channel?.stream_icon} fallback={
            /* Fallback: Show channel number if no icon */
            <View
              width={160}
              height={110}
              color={hexColor(clientBranding().primaryColor)}
              borderRadius={8}
            >
              <Text
                x={80}
                y={40}
                style={{
                  fontSize: 56,
                  fontWeight: "bold",
                  color: hexColor("#ffffff"),
                  textAlign: "center",
                  fontFamily: "Roboto"
                }}
              >
                {props.channel?.num || "0"}
              </Text>
            </View>
          }>
            <View
              width={160}
              height={110}
              color={hexColor("#1E1E1E")}
              borderRadius={8}
            >
              <View
                src={fixImageUrl(props.channel.stream_icon)}
                // src={"https://all.protectionlb1.xyz/Picon/DTT-TIVUSAT/rsi1hd.png"}

                width={90}
                height={90}
                x={35}
                y={10}
                style={{ objectFit: "contain" }}
              />
            </View>
          </Show>

          {/* Channel Number Badge - Small indicator */}
          {/* <View
            x={0}
            y={-30}
            width={80}
            height={40}
            color={hexColor(clientBranding().primaryColor)}
            borderRadius={8}
          >
            <Text
              x={27}
              y={10}
              style={{
                fontSize: 22,
                fontWeight: "bold",
                color: hexColor("#ffffff"),
                textAlign: "center",
                fontFamily: "Roboto"
              }}
            >
              {props.channel?.num || "0"}
            </Text>
          </View> */}

          {/* Channel Name - Display Large (Android TV) */}
          <Text
            x={180}
            y={35}
            style={{
              fontSize: 48,
              fontWeight: "500",
              color: hexColor("#ffffff"),
              fontFamily: "Roboto"
            }}
          >
            {props.channel?.num || "0"} • {props.channel?.name?.length > 26
              ? `${props.channel?.name?.substring(0, 26)}...`
              : props.channel?.name || "Channel"}
          </Text>
        </View>

        {/* Clock — top-right corner, mirrors the channel info row on the
            left. Date below the time, both right-aligned to the OSD's
            edge (60px margin, matching the left margin). */}
        <View x={1600} y={100} style={{ width: 800 }}>
          <Text
            style={{
              fontSize: 48,
              fontWeight: "bold",
              color: hexColor("#ffffff"),
              fontFamily: "Roboto",
              textAlign: "right",
              width: 800
            }}
          >
            {currentTime()}
          </Text>
          <Text
            y={58}
            style={{
              fontSize: 22,
              fontWeight: "normal",
              color: hexColor("#9E9E9E"),
              fontFamily: "Roboto",
              textAlign: "right",
              width: 800
            }}
          >
            {currentDate()}
          </Text>
        </View>

        {/* Current Program Section */}
        <View x={60} y={190}>
          <Show when={epgData() && epgData().epg_listings}>
            {data => {
              const startTime = data()[0]?.start_timestamp;
              const endTime = data()[0]?.stop_timestamp;
              let progress = calculateProgress(startTime, endTime);

              return (
                <View>
                  {/* Program Title first — the primary content, matching
                      how title-then-metadata is ordered elsewhere in this
                      app (ContentBlock, Movies hero, etc.) */}
                  <Text
                    style={{
                      fontSize: 36,
                      fontWeight: "500",
                      color: hexColor("#ffffff"),
                      fontFamily: "Roboto"
                    }}
                  >
                    {truncateText(firstProgram(), 60)}
                  </Text>

                  {/* Eyebrow: NOW PLAYING • start–end, secondary to the
                      title above it */}
                  <Text
                    y={44}
                    style={{
                      fontSize: 20,
                      fontWeight: "500",
                      color: hexColor("#9E9E9E"),
                      fontFamily: "Roboto",
                      letterSpacing: 1
                    }}
                  >
                    NOW PLAYING{firstProgramTime() ? `  •  ${firstProgramTime()}` : ""}
                  </Text>

                  {/* Progress Bar - More prominent */}
                  <View y={90} width={1500} height={8} color={hexColor("#424242")} borderRadius={4}>
                    <View
                      width={1500 * progress}
                      height={8}
                      color={hexColor(clientBranding().primaryColor)}
                      borderRadius={4}
                    />
                  </View>
                </View>
              );
            }}
          </Show>
        </View>

        {/* Up Next Section — a dynamic row of chips, one per upcoming
            program the EPG actually returns, instead of two hand-placed
            "second"/"third" blocks that only ever supported exactly two. */}
        <View x={60} y={310}>
          <Show when={upcomingPrograms().length > 0}>
            <Text
              style={{
                fontSize: 20,
                fontWeight: "500",
                color: hexColor("#9E9E9E"),
                fontFamily: "Roboto",
                letterSpacing: 1
              }}
            >
              UP NEXT
            </Text>

            <View
              y={25}
              style={{ display: "flex", flexDirection: "row", gap: 30, width: 1800 }}
            >
              <For each={upcomingPrograms()}>
                {(program) => (
                  <View style={{ display: "flex", flexDirection: "row", gap: 15, width: 560 }}>
                    <View width={160} height={55} color={hexColor("#2C2C2C")} borderRadius={8}>
                      <Text
                        x={15}
                        y={18}
                        style={{
                          fontSize: 22,
                          fontWeight: "500",
                          color: hexColor("#ffffff"),
                          fontFamily: "Roboto"
                        }}
                      >
                        {program.time}
                      </Text>
                    </View>

                    <Text
                      y={15}
                      style={{
                        fontSize: 26,
                        fontWeight: "400",
                        color: hexColor("#E0E0E0"),
                        fontFamily: "Roboto",
                        maxLines: 1,
                        contain: "width",
                        width: 380
                      }}
                    >
                      {truncateText(program.title, 35)}
                    </Text>
                  </View>
                )}
              </For>
            </View>
          </Show>
        </View>
      </View>
    </View>
  );
};

export default OSD;